import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as Yup from 'yup';
import Input from '../../../../../components/Input';
import DatePicker from '../../../../../components/DatePicker';
import { ARABIC_REGEX_NO_NUMBERS, ENGLISH_REGEX_NO_NUMBERS } from '../../../../../utils/Patterns';

export const useExpenseForm = (mode, selectedExpense, handleAddExpense, handleEditExpense, mimarProfit, lang) => {
    const {
        values,
        setFieldValue,
        handleSubmit,
        touched,
        errors,
        setFieldTouched,
        dirty,
        isValid,
        setTouched,
        validateForm,
      } = useFormik({
        initialValues: {
            expenseNameEn: mode === "edit" && selectedExpense ? selectedExpense.name_en || "" : "",
            expenseNameAr: mode === "edit" && selectedExpense ? selectedExpense.name_ar || "" : "",
            expenseAmount: mode === "edit" && selectedExpense ? selectedExpense.amount || "" : "",
            expenseDate: mode === "edit" && selectedExpense ? selectedExpense.date || "" : "",
        },
        enableReinitialize: true,
        validateOnMount: mode === "edit",
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: Yup.object({
            expenseNameEn: Yup.string()
                .trim()
                .required("enNameRequired")
                .matches(ENGLISH_REGEX_NO_NUMBERS, "englishLettersOnly")
                .max(50, "maximumChars50"),
            expenseNameAr: Yup.string()
                .trim()
                .required("arNameRequired")
                .matches(ARABIC_REGEX_NO_NUMBERS, "arabicLettersOnly")
                .max(50, "maximumChars50"),
            expenseAmount: Yup.number()
                .positive("positiveNumber")
                .required("required")
                .typeError("numbersOnly"),
            expenseDate: Yup.mixed()
                .required("required")
                .test('is-date', 'required', function(value) {
                    // Accept Date objects, valid date strings, or timestamps
                    if (!value) return false;
                    if (value instanceof Date && !isNaN(value)) return true;
                    const date = new Date(value);
                    return date instanceof Date && !isNaN(date);
                })
        }),
        onSubmit: ({ expenseNameEn, expenseNameAr, expenseAmount, expenseDate }) => {
            // Ensure date is properly formatted
            let formattedDate = expenseDate;
            if (expenseDate instanceof Date) {
                formattedDate = expenseDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
            
            const expenseData = {
                name_en: expenseNameEn.trim(),
                name_ar: expenseNameAr.trim(),
                amount: expenseAmount,
                date: formattedDate,
            };

            if (mode === "add") {
                handleAddExpense(expenseData);
            } else {
                handleEditExpense(expenseData);
            }
        },
      });

    // In edit mode, validate and show errors only for invalid fields
    useEffect(() => {
        if (mode === "edit" && selectedExpense) {
            setTimeout(() => {
                validateForm().then((validationErrors) => {
                    if (validationErrors && Object.keys(validationErrors).length > 0) {
                        // Mark only fields with errors as touched
                        const touchedFields = {};
                        Object.keys(validationErrors).forEach(field => {
                            touchedFields[field] = true;
                        });
                        setTouched(touchedFields, false);
                    }
                });
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, selectedExpense]);

    // Render form content
    const renderFormContent = () => (
        <form onSubmit={handleSubmit}>
            <div className="mt-2">
                <Input
                    id="expenseNameEn"
                    name="expenseNameEn"
                    type="text"
                    label={mimarProfit.expenseTable.addEditExpenseModal.expenseNameEn.label}
                    required
                    placeholder={mimarProfit.expenseTable.addEditExpenseModal.expenseNameEn.placeholder}
                    inputWrapperClass="mb-3"
                    value={values["expenseNameEn"]}
                    onChange={(value) => {
                        setFieldTouched('expenseNameEn', true);
                        setFieldValue('expenseNameEn', value);
                    }}
                    isInputHasErr={!!(touched["expenseNameEn"] && errors["expenseNameEn"])}
                    errMsg={errors["expenseNameEn"]}
                />

                <Input
                    id="expenseNameAr"
                    name="expenseNameAr"
                    type="text"
                    label={mimarProfit.expenseTable.addEditExpenseModal.expenseNameAr.label}
                    required
                    placeholder={mimarProfit.expenseTable.addEditExpenseModal.expenseNameAr.placeholder}
                    inputWrapperClass="mb-3"
                    value={values["expenseNameAr"]}
                    onChange={(value) => {
                        setFieldTouched('expenseNameAr', true);
                        setFieldValue('expenseNameAr', value);
                    }}
                    isInputHasErr={!!(touched["expenseNameAr"] && errors["expenseNameAr"])}
                    errMsg={errors["expenseNameAr"]}
                />

                <div className="mt-3 d-flex justify-content-between">
                    <DatePicker
                        value={values["expenseDate"]}
                        onChange={(dateValue) => {
                            setFieldTouched('expenseDate', true);
                            setFieldValue('expenseDate', dateValue);
                        }}
                        name={'expenseDate'}
                        required
                        disablePast={false}
                        label={mimarProfit.expenseTable.addEditExpenseModal.expenseDate.label}
                        disableMinToday={false}
                        placement="right"
                        isInputHasErr={!!(touched["expenseDate"] && errors["expenseDate"])}
                        errMsg={errors["expenseDate"]}
                        disableFuture
                    />

                    <Input
                        id="expenseAmount"
                        name="expenseAmount"
                        type="number"
                        label={mimarProfit.expenseTable.addEditExpenseModal.expenseAmount.label}
                        required
                        placeholder={mimarProfit.expenseTable.addEditExpenseModal.expenseAmount.placeholder}
                        inputWrapperClass="mb-3"
                        value={values["expenseAmount"]}
                        onChange={(value) => {
                            setFieldTouched('expenseAmount', true);
                            setFieldValue('expenseAmount', value);
                        }}
                        isInputHasErr={!!(touched["expenseAmount"] && errors["expenseAmount"])}
                        errMsg={errors["expenseAmount"]}
                    />
                </div>
            </div>
        </form>
    );

    // Handle form reset (similar to ResolveProblemPopup)
    const handleResetForm = () => {
        setFieldValue("expenseNameEn", "");
        setFieldValue("expenseNameAr", "");
        setFieldValue("expenseAmount", "");
        setFieldValue("expenseDate", "");
        setTouched({}, false);
    };

    return {
        renderFormContent,
        handleSubmit,
        dirty,
        isValid,
        handleResetForm
    };
};
